
import type {
  Content,
  JSONContent,
  JSONEditorPropsOptional,
} from '../../components/json-editor.component.vue'
import type { Dictionary, ServicesConfig } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from 'vue'
import JsonEditor, { MenuEnum } from '../../components/json-editor.component.vue'
import { EpbBadge } from '../../components/shared/epb-badge'
import InputLabel from '../../components/shared/input-label.component.vue'
import TransitionSize from '../../components/transition-size.component.vue'
import { mapGetters } from '../../composables'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum } from '../../enums'
import { APP_CONSTS, INSTANCE_MANAGEMENT_CONSTS } from '../../store'
import { FeaturesTreeUtils } from '../../utils'
import { URLUtils } from '../../utils/url.utils'

export default defineComponent({
  name: 'InstanceSearchServiceConfigForm',
  components: {
    EpbBadge,
    InputLabel,
    TransitionSize,
    JsonEditor,
  },
  setup() {
    const ctx = getCurrentInstance()
    const {
      proxy: { $epb, $store, $route },
    } = ctx

    const baseClass = 'epb-instance-search-service-config-form'
    const messages = $epb.messages.instanceManagement.serviceConfig

    const configKeys = ['clientConfig', 'endpointConfigs', 'featureConfigs']
    const activeConfig = ref(configKeys[0])

    const editorProps = ref<Dictionary<JSONEditorPropsOptional>>()

    const { config } = mapGetters($store, INSTANCE_MANAGEMENT_CONSTS.moduleName, {
      config: INSTANCE_MANAGEMENT_CONSTS.getters.searchServiceConfig,
    })

    const { featuresTree } = mapGetters($store, APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    })

    const readonly = computed((): boolean => {
      const featureNode = FeaturesTreeUtils.getFeatureNode(
        $route.meta.featureId,
        featuresTree.value[DomainEnum.GLOBAL],
      )
      return featureNode.permissionLevel !== PermissionLevelEnum.WRITE
    })

    const servicesConfig = CONTAINER.get<ServicesConfig>(TYPES.ServicesConfig)

    const searchServiceManagerUrl = computed(
      (): string | undefined =>
        URLUtils.extractServiceUrl(servicesConfig.searchServiceManager) ?? undefined,
    )

    const documentationText = computed((): string => {
      const linkAddress =
        'https://docs.empathy.co/explore-empathy-platform/configure-empathy-platform/configure-search-service/'
      const linkText = messages.services.search.documentationLinkText
      return $epb.utils.compileMessage(messages.services.search.documentationText, {
        link: `<a href="${linkAddress}" target="_blank">${linkText}</a>`,
      })
    })

    const onChange = (
      content: Content,
      _previousContent: Content,
      status: any,
      fieldId: string,
    ): void => {
      if (!status.contentErrors) {
        editorProps.value[fieldId].content = content
      }
    }

    const activate = (active: string): void => {
      activeConfig.value = activeConfig.value === active ? '' : active
    }

    const initEditorProps = (
      fieldValue: JSONContent['json'],
      fieldId: string,
    ): JSONEditorPropsOptional => {
      const content = {
        json: fieldValue,
      }

      return {
        content,
        readOnly: readonly.value,
        mainMenuBar: false,
        navigationBar: false,
        statusBar: false,
        onChange: (content, previousContent, status) =>
          onChange(content, previousContent, status, fieldId),
      }
    }

    const getConfigValue = (key: string): any => config.value?.[key] ?? {}

    const updateEditorProps = (): void => {
      editorProps.value = configKeys.reduce((prop, key) => {
        prop[key] = initEditorProps(getConfigValue(key), key)
        return prop
      }, {})
    }

    const visibleConfigKeys = computed(() => {
      return config.value ? configKeys : [configKeys[0]]
    })

    watch(
      config,
      () => {
        updateEditorProps()
        if (!config.value) {
          activeConfig.value = configKeys[0]
        }
      },
      { deep: true },
    )

    onMounted(updateEditorProps)

    const getFieldTexts = (id: string): { label: string; description?: string } => {
      const fieldMessages = messages.services.search.fields[id]
      return {
        label: fieldMessages?.label ?? id,
        description: fieldMessages?.description,
      }
    }

    return {
      baseClass,
      messages,
      ColorEnum,
      searchServiceManagerUrl,
      documentationText,
      visibleConfigKeys,
      activeConfig,
      activate,
      MenuEnum,
      editorProps,
      getFieldTexts,
    }
  },
})
